import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import LinearProgress from '@material-ui/core/LinearProgress';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { toast } from 'react-toastify';
import moment from 'moment';

import api from '../../../services';
import { canManageOrganization } from '../../../utils/userRoles';

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 680,
    paddingTop: theme.spacing(2),
  },
  card: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    background: theme.palette.background.paper,
  },
  planCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(1.5),
    cursor: 'pointer',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
    },
  },
  planCardActive: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
    background: theme.palette.action.hover,
  },
  activeBadge: {
    backgroundColor: '#dcfce7',
    color: '#15803d',
    fontWeight: 700,
    fontSize: 11,
    height: 22,
    borderRadius: 6,
  },
  progressBar: {
    height: 6,
    borderRadius: 4,
    backgroundColor: theme.palette.action.hover,
    marginTop: 6,
    marginBottom: 2,
  },
  progressFill: {
    borderRadius: 4,
  },
  cancelBtn: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.light + '22',
      borderColor: theme.palette.error.main,
    },
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
}));

function UsageMeter({ label, used, max }) {
  const classes = useStyles();
  const pct = max > 0 ? Math.min((used / max) * 100, 100) : 0;
  const warn = pct >= 80;

  return (
    <Box mb={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" style={{ fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ fontSize: 12 }}>
          {used} <span style={{ opacity: 0.5 }}>/ {max}</span>
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        className={classes.progressBar}
        classes={{ bar: classes.progressFill }}
        style={{ '& .MuiLinearProgress-bar': { backgroundColor: warn ? '#f59e0b' : undefined } }}
      />
    </Box>
  );
}

export default function SubscriptionTab() {
  const classes = useStyles();
  const canManage = canManageOrganization();
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [plans, setPlans] = useState([]);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showPlans, setShowPlans] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [subRes, plansRes] = await Promise.all([
        api.get('/tenant/subscription'),
        api.get('/tenant/subscription/plans'),
      ]);
      setPayload(subRes.data);
      setPlans(Array.isArray(plansRes.data?.plans) ? plansRes.data.plans : []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not load subscription');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const changePlan = async (key) => {
    if (!canManage) return;
    try {
      const { data } = await api.patch('/tenant/subscription/plan', { planKey: key });
      setPayload(data);
      setShowPlans(false);
      toast.success('Plan updated');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Plan update failed');
    }
  };

  const cancelSubscription = async () => {
    if (!canManage) return;
    setCancelling(true);
    try {
      const { data } = await api.patch('/tenant/subscription/cancel');
      setPayload(data);
      setShowCancel(false);
      toast.success('Subscription cancelled');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Cancel failed');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className={classes.root}>
        <LinearProgress />
      </div>
    );
  }

  const planName = payload?.plan?.displayName || payload?.plan?.key || 'Free';
  const planKey = payload?.plan?.key;
  const jobsUsed = payload?.usage?.jobsCreated || 0;
  const jobsMax = payload?.plan?.maxJobsPerMonth || 0;
  const quotesUsed = payload?.usage?.quotationsCreated || 0;
  const quotesMax = payload?.plan?.maxQuotationsPerMonth || 0;
  const teamUsed = payload?.usage?.teamMembers || 0;
  const teamMax = payload?.effectiveSeatLimit || payload?.plan?.maxTeamMembers || 0;
  const periodEnd = payload?.usage?.periodEnd;

  const otherPlans = plans.filter((p) => p.key !== planKey);
  const hasUpgrade = otherPlans.length > 0;

  return (
    <div className={classes.root}>
      {/* Current plan hero */}
      <div className={classes.card}>
        <Typography className={classes.sectionLabel}>Current plan</Typography>

        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 8 }}>
          <Box>
            <Box display="flex" alignItems="center" style={{ gap: 10, marginBottom: 4 }}>
              <Typography variant="h5" style={{ fontWeight: 700 }}>
                {planName}
              </Typography>
              <Chip label="Active" className={classes.activeBadge} />
            </Box>
            <Typography variant="body2" color="textSecondary">
              {periodEnd
                ? `Renews ${moment(periodEnd).format('MMM D, YYYY')}`
                : 'No renewal date'}
            </Typography>
          </Box>

          {canManage && (
            <Box display="flex" style={{ gap: 8, flexWrap: 'wrap' }}>
              {hasUpgrade && (
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  style={{ borderRadius: 8, fontWeight: 600 }}
                  onClick={() => setShowPlans(true)}
                >
                  Upgrade plan
                </Button>
              )}
              <Button
                variant="outlined"
                className={classes.cancelBtn}
                style={{ borderRadius: 8, fontWeight: 600 }}
                onClick={() => setShowCancel(true)}
              >
                Cancel
              </Button>
            </Box>
          )}
        </Box>
      </div>

      {/* Usage */}
      <div className={classes.card}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography className={classes.sectionLabel} style={{ marginBottom: 0 }}>
            Usage this period
          </Typography>
          {periodEnd && (
            <Typography variant="caption" color="textSecondary">
              Resets {moment(periodEnd).format('MMM D')}
            </Typography>
          )}
        </Box>

        <UsageMeter label="Jobs" used={jobsUsed} max={jobsMax} />
        <UsageMeter label="Quotations" used={quotesUsed} max={quotesMax} />
        <UsageMeter label="Team seats" used={teamUsed} max={teamMax} />
      </div>

      {/* Plan switcher dialog */}
      <Dialog open={showPlans} onClose={() => setShowPlans(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            Change plan
          </Typography>
        </DialogTitle>
        <DialogContent>
          {plans.map((p) => (
            <div
              key={p.key}
              className={`${classes.planCard} ${planKey === p.key ? classes.planCardActive : ''}`}
              onClick={() => changePlan(p.key)}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                    {p.displayName}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {p.maxTeamMembers} seats · {p.maxJobsPerMonth} jobs/mo · {p.maxQuotationsPerMonth} quotes/mo
                  </Typography>
                </Box>
                {planKey === p.key && (
                  <Chip label="Current" className={classes.activeBadge} />
                )}
              </Box>
            </div>
          ))}
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px' }}>
          <Button onClick={() => setShowPlans(false)} style={{ borderRadius: 8 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel confirmation dialog */}
      <Dialog open={showCancel} onClose={() => setShowCancel(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            Cancel subscription?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            Your plan stays active until{' '}
            <strong>{periodEnd ? moment(periodEnd).format('MMM D, YYYY') : 'end of period'}</strong>.
            After that, access will revert to the free tier.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px', gap: 8 }}>
          <Button
            variant="outlined"
            onClick={() => setShowCancel(false)}
            style={{ borderRadius: 8 }}
            disabled={cancelling}
          >
            Keep subscription
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={cancelSubscription}
            disabled={cancelling}
            style={{ borderRadius: 8, backgroundColor: '#dc2626', color: '#fff', fontWeight: 600 }}
          >
            {cancelling ? 'Cancelling…' : 'Yes, cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
